import * as React from 'react';
import * as styles from '../view-segment.module.scss';
import {
  GroupByAggregateFunction,
  GroupByFilter,
  GroupByOperator,
  SegmentAttributeOperator,
  SegmentDetailAttributeDate,
  SegmentDetailAttributeOptions,
  SegmentDetailAttributeRange,
  SegmentDetailAttributeTransactional,
  SegmentFilterOperator
} from '../../../models/segments';
import moment from 'moment';
import clsx from 'clsx';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import TransactionIndicatorIcon from '../../../images/icons/transaction-indicator-icon';

interface Props {
  attribute:
    | SegmentDetailAttributeOptions
    | SegmentDetailAttributeRange
    | SegmentDetailAttributeDate
    | SegmentDetailAttributeTransactional;
  depth: number;
  inlineOperator?: SegmentAttributeOperator;
}

const useStyles = makeStyles(() => ({
  viewAttributeContainer: {
    width: '100%'
  },
  groupByContainer: {
    padding: '5px 0px 5px 40px',
    display: 'flex',
    flexDirection: 'column'
  },
  groupByName: {
    fontWeight: 600
  },
  groupByValuesRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px'
  },
  groupByValuesName: {
    flex: '0 0 300px'
  },
  groupByValuesAggregateFunction: {
    width: '100%',
    textAlign: 'center',
    flex: '0 0 100px'
  },
  groupByValuesOperator: {
    width: '100%',
    textAlign: 'center',
    flex: '0 0 150px'
  }
}));

const ViewAttribute = ({ attribute, depth, inlineOperator }: Props) => {
  const classes = useStyles();
  const matches = useMediaQuery('(min-width: 1200px)');
  return (
    <div className={classes.viewAttributeContainer}>
      <ul className={styles.attribute}>
        <li
          className={styles.attributeName}
          style={{ flex: `0 0 ${(matches ? 300 : 200) - depth * 12}px` }}
          title={attribute.name}
        >
          {inlineOperator !== undefined && (
            <span className={clsx(styles.operator, styles.inlineOperator)}>
              {SegmentAttributeOperator[inlineOperator]}
            </span>
          )}
          {attribute.isTransactional && (
            <>
              <TransactionIndicatorIcon />
              &nbsp;
            </>
          )}
          {attribute.name}
        </li>
        <li className={clsx(styles.operator, styles.filterOperator)}>
          {SegmentFilterOperator[attribute.filterOperator]}
        </li>
        {!attribute.isTransactional && <li className={styles.filterText}>{filtersToText(attribute)}</li>}
        {!!attribute.isTransactional && <>{transactionalToText(attribute)}</>}
      </ul>
      {!!attribute.isTransactional && !!attribute.groupBy && <ViewGroupBy groupBy={attribute.groupBy} />}
    </div>
  );
};

export default ViewAttribute;

/**
 * Render appropriate text representing selected filters based on the filter type
 */
function filtersToText(
  attribute:
    | SegmentDetailAttributeOptions
    | SegmentDetailAttributeRange
    | SegmentDetailAttributeDate
    | SegmentDetailAttributeTransactional
) {
  if (attribute.isTransactional === true) {
    return transactionalToText(attribute);
  }

  switch (attribute.filterType) {
    case 'options': {
      return optionsToText(attribute);
    }
    case 'range': {
      return rangeToText(attribute);
    }
    case 'datetime': {
      return dateRangeToText(attribute);
    }
    default:
      return '';
  }
}

/**
 * Render options type filter into a comma separated list, transforming empty strings into a special blank value
 */
function optionsToText(attribute: SegmentDetailAttributeOptions) {
  if (Array.isArray(attribute.selectedFilters)) {
    if (attribute.selectedFilters.findIndex((filterValue) => filterValue.trim() === '') !== -1) {
      const filters = attribute.selectedFilters.filter((filter) => filter.trim() !== '');
      return (
        <React.Fragment>
          <span className={styles.blankOption}>blank</span>
          {filters.length > 0 && `, ${filters.join(', ')}`}
        </React.Fragment>
      );
    } else {
      return attribute.selectedFilters.length > 0
        ? attribute.selectedFilters.join(', ')
        : 'All - No additional filtering applied';
    }
  } else if (typeof attribute.selectedFilters === 'string') {
    return attribute.selectedFilters;
  } else {
    return '';
  }
}

/**
 * Render numerical range filters to a comma delimited Min/Max string
 */
function rangeToText(attribute: SegmentDetailAttributeRange) {
  let rangeText = '';

  if (attribute.selectedFilters) {
    const rangeArray = [];
    if (attribute.selectedFilters.min) {
      rangeArray.push(`Min: ${attribute.selectedFilters.min}`);
    }
    if (attribute.selectedFilters.max) {
      rangeArray.push(`Max: ${attribute.selectedFilters.max}`);
    }
    rangeText = rangeArray.join(', ');
  }

  if (rangeText === '') {
    return 'All - No additional filtering applied';
  }

  return rangeText;
}

/**
 * Render datetime range filters to a comma delimited Start/End string
 */
function dateRangeToText(attribute: SegmentDetailAttributeDate) {
  if (typeof attribute.selectedFilters == 'number') {
    return `Last ${attribute.selectedFilters} days`;
  }

  let dateRangeText = '';
  if (attribute.selectedFilters) {
    const dateRangeArray = [];
    if (attribute.selectedFilters.min) {
      dateRangeArray.push(`Start: ${moment(attribute.selectedFilters.min).format('MM/DD/YYYY')}`);
    }
    if (attribute.selectedFilters.max) {
      dateRangeArray.push(`End: ${moment(attribute.selectedFilters.max).format('MM/DD/YYYY')}`);
    }
    dateRangeText = dateRangeArray.join(', ');
  }

  if (dateRangeText === '') {
    return 'All - No additional filtering applied';
  }

  return dateRangeText;
}

/**
 * Render transactional filters to a where string
 */
function transactionalToText(_attribute: SegmentDetailAttributeTransactional) {
  if (
    !_attribute.selectedTransactionalFilters?.predicates ||
    _attribute.selectedTransactionalFilters.predicates.length === 0
  ) {
    return <li className={styles.filterText}>All - No additional filtering applied</li>;
  }
  let attributeText: JSX.Element[] = [];
  const attributesLength = _attribute.selectedTransactionalFilters.predicates.length;
  _attribute.selectedTransactionalFilters.predicates.forEach((filter, index) => {
    //@ts-ignore
    const filterName = `${filter.name}`;
    attributeText.push(
      <div key={filter.filterId} className={styles.transactionalAttributeContainer}>
        <span className={styles.transactionalFilterName}>{filterName}</span>
        <span className={styles.operator}>{SegmentFilterOperator[filter.filterOperator]}</span>
        <li className={styles.filterText}>
          {filtersToText(
            filter as SegmentDetailAttributeOptions | SegmentDetailAttributeRange | SegmentDetailAttributeDate
          )}
        </li>
        {index !== attributesLength - 1 && (
          <span className={styles.transactionalOperator}>
            {SegmentAttributeOperator[_attribute.selectedTransactionalFilters.operator]}
          </span>
        )}
      </div>
    );
  });

  return (
    <div key={_attribute.attributeId} className={styles.transactionalRow}>
      {attributeText}
    </div>
  );
}

interface ViewGroupByProps {
  groupBy: GroupByFilter;
}

const ViewGroupBy = ({ groupBy }: ViewGroupByProps) => {
  const classes = useStyles();

  return (
    <div className={classes.groupByContainer}>
      <div>
        Group By: <span className={classes.groupByName}>{groupBy.groupByFilterName}</span>
      </div>
      {groupBy.groupByValues.groupByPredicates.map((values, index) => {
        return (
          <div className={classes.groupByValuesRow} key={`${values.valueFilterId} - ${index}`}>
            <div className={classes.groupByValuesName}>{values.valueFilterName}</div>
            <div className={classes.groupByValuesAggregateFunction}>
              {GroupByAggregateFunction[values.aggregateFunction]}
            </div>
            <div className={classes.groupByValuesOperator}>{GroupByOperator[values.aggregateOperator]}</div>
            <div>{values.value}</div>
          </div>
        );
      })}
    </div>
  );
};
